import { service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { advancedFiltersMovedTour } from 'tio-common/utils/tours/advanced-filters-moved-tour';
import ProgramsPage from 'tio-reporting/components/tas/programs-page';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import ViewToggle from 'tio-reporting/components/view-toggle';
import DatasetSelector from 'tio-reporting/components/dataset-selector';
import TourTrigger from 'tio-common/components/tours/tour-trigger';
import ENV from 'tio-employee/config/environment';
import type SessionContext from 'tio-employee/services/session-context';
import type { Company } from 'tio-data/data/resources/company.type';
import type TasProgramsController from 'tio-employee/controllers/authenticated/admin/reporting/tas-programs';
import type TourManagerService from 'tio-common/services/tour-manager';

export interface ReportingTasProgramsRouteSignature {
  Args: {
    model: Company;
    controller: TasProgramsController;
  };
}

export default class ReportingTasProgramsRouteComponent extends Component<ReportingTasProgramsRouteSignature> {
  @service declare sessionContext: SessionContext;
  @service declare tourManager: TourManagerService;

  constructor(owner: unknown, args: ReportingTasProgramsRouteSignature['Args']) {
    // @ts-expect-error - Owner type mismatch is ok here
    super(owner, args);
    this.registerTours();
  }

  private registerTours() {
    // Register the advanced filters tour
    // In the future, these tour configs would come from a database via API
    this.tourManager.registerTour(advancedFiltersMovedTour);
  }

  get companyId() {
    return this.args.model?.id;
  }

  get showColumnSelector() {
    return Boolean(ENV.featureFlags.reportingColumnSelector);
  }

  get showViewToggle() {
    return Boolean(ENV.featureFlags.reportingChartView);
  }

  get showSaveReports() {
    return Boolean(ENV.featureFlags.reportingSaveReports);
  }

  <template>
    {{! Tour trigger for advanced filters location }}
    <TourTrigger @tourId="advanced-filters-moved" @autoStart={{true}} @delay={{500}} />

    <ProgramsPage
      @company={{@model}}
      @controller={{@controller}}
      @baseRoute="authenticated.admin.tuition-assistance"
      @applicationDetailRoute="authenticated.admin.tas.applications.approval"
      @programDetailRoute="authenticated.admin.tuition-assistance.program-instances.show"
      @programTemplateDetailRoute="authenticated.admin.tuition-assistance.program-templates.show"
      @peopleDetailRoute="authenticated.admin.people.show"
      @documentDetailRoute="authenticated.observability.documents.show"
      @showViewToggle={{this.showViewToggle}}
      @showSaveReports={{this.showSaveReports}}
      @showColumnSelector={{this.showColumnSelector}}
    >
      <:breadcrumbs>
        <TioPageBreadcrumbs as |b|>
          <b.crumb
            @route="authenticated.admin.reporting.index"
            @label={{t "common.reporting.default"}}
          />
        </TioPageBreadcrumbs>
      </:breadcrumbs>

      <:headerActions as |actions|>
        {{#if this.showViewToggle}}
          <DatasetSelector
            @selectedDataset={{actions.selectedDataset}}
            @onDatasetChange={{actions.onDatasetChange}}
          />
          <ViewToggle
            @currentView={{actions.currentView}}
            @onViewChange={{actions.onViewChange}}
            @chartDisabled={{false}}
          />
        {{/if}}
      </:headerActions>
    </ProgramsPage>
  </template>
}
